import ms from 'ms';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useToggle } from 'react-use';
import { DragAndDropProps, UploadOptions } from '.';
import { FILE_SIZE_LIMIT } from '../../../constants';

type UseViewModelReturn = {
  handleOnConfirm: (options: UploadOptions) => void;
  isDragging: boolean;
  handleDragEnter: (e: React.DragEvent<HTMLButtonElement>) => void;
  handleDragLeave: () => void;
  handleDrop: (e: React.DragEvent<HTMLButtonElement>) => void;
  handleBrowseFile: (event: ChangeEvent<HTMLInputElement>) => void;
  on: boolean;
  toggle: (value: boolean) => void;
  alertMessage?: string;
};

const useViewModel = ({ onUpload }: DragAndDropProps): UseViewModelReturn => {
  const [isDragging, setIsDragging] = useState(false);
  const [on, toggle] = useToggle(false);
  const [file, setFile] = useState<File>();
  const [alertMessage, setAlertMessage] = useState<string>();

  const handleDragEnter = (e: React.DragEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (): void => {
    setIsDragging(false);
  };

  const isValidFileType = (fileDrop: File): boolean => {
    const allowedFileTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];

    return allowedFileTypes.includes(fileDrop.type);
  };

  const handleFile = (files: File): void => {
    const fileSizeInMB = files.size / (1024 * 1024);
    if (fileSizeInMB <= FILE_SIZE_LIMIT) {
      setFile(files);
      toggle(true);
    } else {
      setAlertMessage('File size should not exceed 10 MB!');
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setIsDragging(false);
    const fileDrop = e.dataTransfer.files[0];

    if (fileDrop && isValidFileType(fileDrop)) {
      handleFile(fileDrop);
    } else {
      setAlertMessage('The file should be only XLXS format');
    }
  };

  const handleBrowseFile = (event: ChangeEvent<HTMLInputElement>): void => {
    const fileInput = event.target as HTMLInputElement;

    if (!fileInput?.files || fileInput.files.length === 0) {
      setAlertMessage('Please select a file.');
    } else if (!isValidFileType(fileInput.files[0])) {
      setAlertMessage('The file should be only XLXS format');
    } else {
      handleFile(fileInput.files[0]);
      fileInput.value = '';
    }
  };

  const handleOnConfirm = (options: UploadOptions): void => {
    toggle(false);
    onUpload(file, options);
  };

  useEffect(() => {
    if (alertMessage) {
      const timeout = setInterval(() => {
        clearInterval(timeout);
        setAlertMessage(undefined);
      }, ms('60s'));
    }
  }, [alertMessage]);

  return {
    handleOnConfirm,
    isDragging,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleBrowseFile,
    on,
    toggle,
    alertMessage,
  };
};

export default useViewModel;
